import { BaseScreen, waitFor } from '@goodboydigital/astro';
import { Container, Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import { RectangleButton } from '../ui/buttons/RectangleButton';
import { Logo } from '../ui/Logo';
import { RankPanel } from '../ui/RankPanel';
import { RewardsPanel } from '../ui/RewardsPanel';
import { Random } from '../utils/Random';
import { Browser } from '../utils/Browser';
import { MenuClouds } from '../ui/MenuClouds';
import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import LocalisedText from '../ui/LocalisedText';

export class TitleScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public logo!: Logo;
    public playButton!: RectangleButton;
    public settingsButton!: NineSliceButton;
    
    private frontHill!: Sprite;
    private characterContainer!: Container;
    private robin!: Sprite;
    private gumball!: Sprite;
    
    private _firstTime: boolean;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        this._firstTime = false;
        
        gsap.registerPlugin(CustomEase);

    }

    async init(): Promise<void>
    {
        this.app.screens.main.screens.game.instance.view.y = 1500;
        Shared.game.scenario.viewBg.y = -500;
        Shared.game.sceneView.entitiesContainer.scale.set(0.75);
        
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);
        this.view.sortableChildren = true;
        
        this.frontHill = new Sprite(Shared.assets.getTexture('title_front_hill'));
        this.frontHill.anchor.set(0.5, 1);
        this.frontHill.y = this.app.stageBottom;
        this.view.addChild(this.frontHill);
        
        var starfire = new Sprite(Shared.assets.getTexture('title_starfire'));
        starfire.anchor.set(0.5, 1);
        starfire.x = 150;
        starfire.y = -100;
        this.frontHill.addChild(starfire);
        
        this.characterContainer = new Container();
        this.characterContainer.x = this.app.stageCenter.x;
        this.characterContainer.y = this.app.stageCenter.y;
        this.view.addChild(this.characterContainer);
        
        this.robin = new Sprite(Shared.assets.getTexture('title_robin'));
        this.robin.anchor.set(0.5);
        this.robin.y = this.app.stageBottom + this.robin.height;
        this.characterContainer.addChild(this.robin);
        
        this.gumball = new Sprite(Shared.assets.getTexture('title_gumball'));
        this.gumball.anchor.set(0.5);        
        this.characterContainer.addChild(this.gumball);
        this.robin.x = Math.min(-((this.app.stageRight - this.app.stageLeft) * 0.5) + (170), -460);
        this.robin.y = 0;
        
        this.gumball.x =  Math.max(((this.app.stageRight - this.app.stageLeft) * 0.5) - (170), 370);
        this.gumball.y = 0;
        
        this.playButton = new RectangleButton({
            text: 'titleScreen_play',
            bg: 'magenta',
            w: 350,
            h: 130,
            fontSize: 70,
        });
        this.view.addChild(this.playButton.view);
        this.playButton.onPress.connect(async () =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.GAME_START, AnalyticsState.HOMEPAGE.persistentMetaData);
            this.settingsButton.hide();
            this.playButton.hide(true);
            gsap.to(this.logo.scale, { x: 1.75, y: 1.75, duration: 1.0, ease: 'linear' });
        
            gsap.to(this.characterContainer.scale, { x: 1.75, y: 1.75, duration: 1.0, ease: 'linear' });
            gsap.to(this.frontHill.scale, { x: 1.5, y: 1.5, duration: 1.0, ease: 'linear' }); 
            gsap.to(Shared.game.sceneView.entitiesContainer.scale, { x: 1, y: 1, duration: 1.0, ease: 'linear' }); 
                
            gsap.to(this.app.screens.main.screens.game.instance.view, { y: 0, duration: 1.0, ease: CustomEase.create("custom", "M0,0,C0.248,0,0.248,1.242,1,1") });
            gsap.to(Shared.game.scenario.viewBg, { y: 0, duration: 1.0, ease: CustomEase.create("custom", "M0,0,C0.248,0,0.248,1.242,1,1") });
            
            gsap.to(this.logo, { y: -300, duration: 1, ease: 'power3.inOut'});
            gsap.to(this.characterContainer, { y: this.app.stageBottom + 600, duration: 1, ease: CustomEase.create("custom", "M0,0 C0.632,-0.03 0.74,1 1,1 ")});
            gsap.to(this.frontHill, { y: this.app.stageBottom + 700, duration: 1, ease: CustomEase.create("custom", "M0,0 C0.632,-0.03 0.74,1 1,1 ")});
            await waitFor(1000);
            
            if(!this.app.settings.hasPlayerSetName && !Settings.browserLite){
                this.app.controller.toChangeName(true, false, true);
            }
            else{
                this.app.controller.toHome();
            }
            
            //
        });

        const settingsIcon = Sprite.from(this.app.assets.getTexture('icon-settings'));

        this.settingsButton = new NineSliceButton({
            icon: settingsIcon,
            accessibleTitle: 'Back',
            w: settingsIcon.width * 1.5,
            h: settingsIcon.height * 1.5,
            clickAudio: 'btn-settings-click',
        });
        this.view.addChild(this.settingsButton.view);
        this.settingsButton.onPress.connect(() =>
        {
            this.settingsButton.hide();
            this.playButton.hide(true);
            Browser.lockOrientation('landscape');
            this.app.analytics.trackEvent(AnalyticsEvent.HOMEPAGE_SETTINGS_CLICK);
            this.app.controller.toSettings(true);
        });
        this.view.addChild(this.settingsButton.view);

        this.logo = new Logo();
        this.view.addChild(this.logo);
        
        this.app.analytics.trackState(AnalyticsState.HOMEPAGE);
        
    }
    
    async prepare(screenData: Record<string, any>): Promise<void>
    {
        this._firstTime = screenData.firstTime ?? false;
        
    }
    
    public update(delta = 1): void
    {
        this.app.clouds?.update(delta);
    }

    async show(): Promise<void>
    {
        
        this.resize(this.width, this.height);

        this.view.visible = true;
        
        this.playButton.view.visible = false;
        this.logo.container.renderable = false;

        Shared.game.soundSystem.playMusic('menu-music');
        
        this.settingsButton.show();
        this.logo.refresh();
        
        if(this._firstTime){
            this.view.y = 1000;
            
            var transitionDownEase = 'sine.out';
            gsap.to(this.app.screens.main.screens.game.instance.view, { y: 750, duration: 1.0, ease: transitionDownEase });
            gsap.to(Shared.game.scenario.viewBg, { y: -750, duration: 1.0, ease: transitionDownEase });
            gsap.to(this.view, { y: 0, duration: 1.0, ease: transitionDownEase });
            
            if(this.app.clouds){
                gsap.to(this.app.clouds.view, { y: -1000, duration: 1.0, ease: transitionDownEase });
            }
        }
        
        this.showElements();
        
    }
    
    async showElements(): Promise<void>
    {
        if(this._firstTime)
            await waitFor(1500);
        
        this.app.hideMenuClouds();
        await this.logo.show();
        
        this.playButton.show(true, 0.3);
    }

    async hide(): Promise<void>
    {
        
    }

    hidden(): void
    {
        this.view.visible = false;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.playButton.view.position.set(
            this.app.stageCenter.x,
            this.app.stageBottom - 200,
        );

        this.settingsButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );

        this.logo.x = this.app.stageCenter.x;
        this.logo.y = 150;
        
        this.app.clouds?.resize(w, h);
        
        this.frontHill.x = this.app.stageCenter.x;
    }
}
